"use client";

import { motion, useTransform, useScroll, useSpring } from "framer-motion";
import React, { useRef, useState, useEffect } from "react";
import { useLanguage } from "@/context/language-context";
import { useMediaQuery, BREAKPOINTS } from "@/hooks/use-media-query";
import { BlurReveal } from "@/components/blur-reveal";
import { ProjectModal } from "@/components/project-modal";

export type ProjectItem = {
  id: string;
  title: string;
  category: string;
  year: string;
  description: string;
  image: string;
  demo?: string;
  repo?: string;
  stack?: string[];
};

export default function Projects() {
  const { content } = useLanguage();

  const isDesktop = useMediaQuery(BREAKPOINTS.xl);

  const targetRef = useRef<HTMLDivElement>(null);
  const horizontalContainerRef = useRef<HTMLDivElement>(null);

  const [measurements, setMeasurements] = useState({ scrollRange: 0, dynamicHeight: "auto" });
  const [selectedProject, setSelectedProject] = useState<ProjectItem | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    if (!isDesktop) {
      setMeasurements({ scrollRange: 0, dynamicHeight: "auto" });
      return;
    }

    const updateMeasurements = () => {
      if (horizontalContainerRef.current) {
        const totalWidth = horizontalContainerRef.current.scrollWidth;
        const viewportW = window.innerWidth;
        const range = totalWidth - viewportW;
        const safeRange = range > 0 ? range : 0;

        setMeasurements({
          scrollRange: safeRange,
          dynamicHeight: `${safeRange + window.innerHeight}px`,
        });
      }
    };

    updateMeasurements();

    const timeout = setTimeout(updateMeasurements, 100);
    const resizeObserver = new ResizeObserver(() => {
      requestAnimationFrame(updateMeasurements);
    });

    if (horizontalContainerRef.current) {
      resizeObserver.observe(horizontalContainerRef.current);
    }

    return () => {
      clearTimeout(timeout);
      resizeObserver.disconnect();
    };
  }, [isDesktop, content.projects.items]);

  const { scrollYProgress } = useScroll({
    target: targetRef,
    offset: ["start start", "end end"],
  });

  const x = useTransform(scrollYProgress, [0, 1], [0, -measurements.scrollRange]);
  const smoothX = useSpring(x, { stiffness: 400, damping: 60, restDelta: 0.5 });

  const handleOpenProject = (project: ProjectItem) => {
    setSelectedProject(project);
    setIsModalOpen(true);
  };

  return (
    <section
      ref={targetRef}
      data-slot="projects"
      className="relative py-16 md:py-24 lg:py-32 xl:py-0"
      style={{ height: measurements.dynamicHeight }}
    >
      <div
        className={`w-full ${
          isDesktop
            ? "sticky top-0 flex h-screen items-center overflow-hidden"
            : "relative flex flex-col"
        } `}
      >
        {!isDesktop ? (
          <>
            <div className="mb-10 flex flex-col gap-4 px-container">
              <BlurReveal>
                <span className="title-counter">[003]</span>
              </BlurReveal>

              <BlurReveal>
                <h2 className="title">{content.projects.title}</h2>
              </BlurReveal>

              <BlurReveal>
                <p className="mt-4 text-lg text-muted-foreground">{content.projects.intro}</p>
              </BlurReveal>
            </div>
            <div className="flex w-full max-w-full flex-col gap-container px-container">
              {content.projects.items.map((project: ProjectItem) => (
                <ProjectCard
                  key={project.id}
                  project={project}
                  onClick={() => handleOpenProject(project)}
                />
              ))}
            </div>
          </>
        ) : (
          <motion.div
            ref={horizontalContainerRef}
            style={{ x: smoothX }}
            className="flex w-max items-center px-container"
          >
            <div className="flex w-[60vw] shrink-0 flex-col justify-center xl:w-[40vw]">
              <div className="flex flex-col gap-4">
                <BlurReveal>
                  <span className="title-counter">[003]</span>
                </BlurReveal>

                <BlurReveal>
                  <h2 className="title">{content.projects.title}</h2>
                </BlurReveal>

                <BlurReveal>
                  <p className="mt-4 text-5xl leading-tight font-light">{content.projects.intro}</p>
                </BlurReveal>

                <BlurReveal>
                  <div className="mt-12 flex items-center gap-4">
                    <div className="h-px w-24 bg-border" />
                    <span className="font-mono text-sm text-foreground/40">
                      {content.projects.scroll_text}
                    </span>
                  </div>
                </BlurReveal>
              </div>
            </div>

            {content.projects.items.map((project: ProjectItem) => (
              <ProjectCard
                key={project.id}
                project={project}
                onClick={() => handleOpenProject(project)}
              />
            ))}
          </motion.div>
        )}
      </div>

      <ProjectModal open={isModalOpen} onOpenChange={setIsModalOpen} project={selectedProject} />
    </section>
  );
}

const ProjectCard = React.memo(function ProjectCard({
  project,
  onClick,
}: {
  project: ProjectItem;
  onClick?: () => void;
}) {
  return (
    <BlurReveal>
      <div
        onClick={onClick}
        className="group perspective-1000 relative aspect-4/3 w-full shrink-0 cursor-pointer xl:mx-[calc(var(--container-spacing)/2)] xl:w-[45vw]"
      >
        <div className="relative h-full w-full overflow-hidden border border-border/50 bg-muted transition-all duration-700 ease-out group-hover:border-foreground/20">
          <div className="absolute inset-0 z-0">
            <img
              src={project.image}
              alt={`${project.title} project screenshot`}
              loading="lazy"
              className="absolute inset-0 h-full w-full object-cover object-top transition-transform duration-1000 group-hover:scale-[1.03]"
            />
            <div
              className="absolute inset-0 bg-linear-to-t from-background from-10% via-background/60 via-35% to-transparent to-55%"
              aria-hidden
            />
          </div>

          <div className="absolute inset-0 z-10 flex flex-col justify-between p-6 xl:p-12">
            <div className="flex items-start justify-between">
              <div className="overflow-hidden">
                <span className="block translate-y-full transform font-mono text-xs tracking-widest text-muted-foreground uppercase transition-transform delay-100 duration-500 group-hover:translate-y-0 xl:text-sm">
                  {project.category}
                </span>
              </div>
              <div className="overflow-hidden">
                <span className="block translate-y-full transform font-mono text-xs text-muted-foreground transition-transform delay-200 duration-500 group-hover:translate-y-0 xl:text-sm">
                  {project.year}
                </span>
              </div>
            </div>

            <div className="space-y-2 xl:space-y-3">
              <h3 className="text-2xl font-black tracking-tighter text-foreground uppercase transition-opacity duration-500 sm:text-3xl md:text-4xl xl:text-5xl">
                {project.demo ? (
                  <a
                    href={project.demo}
                    target="_blank"
                    rel="noopener"
                    onClick={(e) => e.stopPropagation()}
                    className="transition-opacity hover:opacity-80"
                  >
                    {project.title}
                  </a>
                ) : (
                  project.title
                )}
              </h3>
              <p className="line-clamp-3 max-w-xl text-sm leading-relaxed text-muted-foreground xl:text-base">
                {project.description}
              </p>
              {(project.demo || project.repo) && (
                <div className="flex flex-wrap gap-x-4 gap-y-1 pt-1">
                  {project.demo && (
                    <a
                      href={project.demo}
                      target="_blank"
                      rel="noopener"
                      onClick={(e) => e.stopPropagation()}
                      className="font-mono text-xs tracking-widest text-foreground/70 uppercase transition-colors hover:text-foreground xl:text-sm"
                    >
                      Visit site
                    </a>
                  )}
                  {project.repo && (
                    <a
                      href={project.repo}
                      target="_blank"
                      rel="noopener"
                      onClick={(e) => e.stopPropagation()}
                      className="font-mono text-xs tracking-widest text-foreground/70 uppercase transition-colors hover:text-foreground xl:text-sm"
                    >
                      Source
                    </a>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </BlurReveal>
  );
});
